import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { AssignedRevisedCopyReview, revisedCopyByDeskService, SubmissionRevisionRequest } from '../services/adminAuth';
import { CheckCircle, Clock, Eye, FileText, Download, Loader2, AlertTriangle } from 'lucide-react';
import Drawer from '../../sharedComponents/Drawer';
import { Modal } from '../components/adminModals';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { FILE_BASE_URL } from '../../../api/axios';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    Pending: { label: 'Pending', className: 'bg-yellow-50 text-yellow-700 border border-yellow-200' },
    Submitted: { label: 'Submitted', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
    AcceptableForPublication: { label: 'Acceptable for Publication', className: 'bg-green-50 text-green-700 border border-green-200' },
    ReturnToAuthorForRevisedCopy: { label: 'Return to Author for Revised Copy', className: 'bg-orange-50 text-orange-700 border border-orange-200' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const cfg = STATUS_CONFIG[status] ?? { label: status, className: 'bg-gray-100 text-gray-600 border border-gray-200' };
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.className}`}>
            {cfg.label}
        </span>
    );
};

const resolveUrl = (url: string) =>
    url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;

const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
};

const Spinner = () => (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

// ─────────────────────────────────────────────────────────────────────────────

function AssignedRevisionAssessments() {
    const { showNotification } = useNotification();

    const [submissions, setSubmissions] = useState<AssignedRevisedCopyReview[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Drawer state for viewing revision history
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerItems, setDrawerItems] = useState<SubmissionRevisionRequest[]>([]);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [downloadingMap, setDownloadingMap] = useState<Record<string, boolean>>({});
    const [selectedSubmissionTitle, setSelectedSubmissionTitle] = useState('');
    const [selectedSubmissionNumber, setSelectedSubmissionNumber] = useState('');

    // Assessment Modal state
    const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
    const [assessmentTarget, setAssessmentTarget] = useState<AssignedRevisedCopyReview | null>(null);
    const [assessmentRecommendation, setAssessmentRecommendation] = useState<'' | '2' | '3'>('');
    const [assessmentComment, setAssessmentComment] = useState('');
    const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);

    // ── Fetch submissions ──────────────────────────────────────────────────────

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await revisedCopyByDeskService.GetAssignedRevisedCopyAssignmentByMember({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch assigned submissions',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching submissions',
            });
        } finally {
            setLoading(false);
        }
    };

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleDownload = async (url: string, key: string, filename: string) => {
        setDownloadingMap(prev => ({ ...prev, [key]: true }));
        try {
            await downloadFileFromUrl(resolveUrl(url), filename);
        } catch (error) {
            showNotification('error', {
                title: 'Download Error',
                message: 'Failed to download file',
            });
        } finally {
            setDownloadingMap(prev => ({ ...prev, [key]: false }));
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    // ── View Revision History Drawer ───────────────────────────────────────────
    const handleViewRevisionHistory = async (submission: AssignedRevisedCopyReview) => {
        setSelectedSubmissionTitle(submission.submissionTitle);
        setSelectedSubmissionNumber(submission.submissionNumber);
        setDrawerItems([]);
        setIsDrawerOpen(true);
        setDrawerLoading(true);
        
        try {
            const response = await revisedCopyByDeskService.getRevisionRequestsBySubmissionId(submission.submissionId);
            if (response.isSuccess && response.data) {
                setDrawerItems(response.data);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to load revision history',
                });
            }
        } catch (error) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred',
            });
        } finally {
            setDrawerLoading(false);
        }
    };

    // ── Perform Revision Assessment Modal ──────────────────────────────────────
    const openAssessmentModal = (submission: AssignedRevisedCopyReview) => {
        setAssessmentTarget(submission);
        setAssessmentRecommendation('');
        setAssessmentComment('');
        setIsAssessmentModalOpen(true);
    };

    const closeAssessmentModal = () => {
        if (isSubmittingAssessment) return;
        setIsAssessmentModalOpen(false);
        setAssessmentTarget(null);
        setAssessmentRecommendation('');
        setAssessmentComment('');
    };

    const handleSubmitAssessment = async () => {
        if (!assessmentTarget || !assessmentRecommendation) {
            showNotification('error', {
                title: 'Required',
                message: 'Please select a recommendation before submitting.',
            });
            return;
        }

        setIsSubmittingAssessment(true);
        try {
            const response = await revisedCopyByDeskService.submitRevisedCopyMemberReview({
                revisedCopyReviewId: assessmentTarget.revisedCopyReviewId,
                comment: assessmentComment,
                recommendation: parseInt(assessmentRecommendation, 10),
            });

            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Assessment Submitted',
                    message: 'Your revision assessment has been submitted successfully.',
                });
                closeAssessmentModal();
                fetchSubmissions(); // Refresh the list
            } else {
                showNotification('error', {
                    title: 'Submission Failed',
                    message: response.message || 'Failed to submit revision assessment.',
                });
            }
        } catch (error) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while submitting your assessment.',
            });
        } finally {
            setIsSubmittingAssessment(false);
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const getSubmissionStatusBadge = (isSubmitted: boolean, reviewedAt: string | null) => {
        if (isSubmitted) {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Submitted
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700">
                <Clock className="w-3.5 h-3.5" />
                Pending Review
            </span>
        );
    };

    // ── Table columns based on AssignedRevisedCopyReview interface ─────────────

    const columns: TableColumn<AssignedRevisedCopyReview>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (_, row) => (
                <span className="text-gray-500 text-sm font-mono">{row.submissionNumber}</span>
            ),
        },
        {
            key: 'submissionTitle',
            label: 'Title / Authors',
            render: (_, row) => (
                <div>
                    <div className="font-medium text-gray-900">{row.submissionTitle}</div>
                    {row.bookAuthors && row.bookAuthors.length > 0 ? (
                        <div className="mt-0.5 text-sm text-gray-500">
                            {row.bookAuthors.map(a => a.name).join(', ')}
                        </div>
                    ) : null}
                </div>
            ),
        },
        {
            key: 'dateAssigned',
            label: 'Date Assigned',
            render: (value, row) => (
                <span className="text-sm text-gray-700">{formatDate(row.dateAssigned)}</span>
            ),
        },
        // {
        //     key: 'reviewedAt',
        //     label: 'Reviewed At',
        //     render: (value, row) => (
        //         <span className="text-sm text-gray-700">
        //             {formatDate(row.reviewedAt)}
        //         </span>
        //     ),
        // },
        {
            key: 'isSubmitted',
            label: 'Status',
            render: (value, row) => getSubmissionStatusBadge(row.isSubmitted, row.reviewedAt),
        },
        {
            key: 'recommendation',
            label: 'Recommendation',
            render: (value, row) => (
                <span className="text-sm text-gray-700">
                    {row.recommendation || <span className="text-gray-400 italic">Not provided</span>}
                </span>
            ),
        },
    ];

    // Actions: View Revision History + Perform Revision Assessment (only when pending)
    const actions: TableAction<AssignedRevisedCopyReview>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Revision History',
                    onClick: handleViewRevisionHistory,
                    className: 'text-blue-600',
                },
                {
                    label: 'Perform Revision Assessment',
                    onClick: openAssessmentModal,
                    className: (row) => !row.isSubmitted ? 'text-blue-600' : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Revised Copy Assessments
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Revised copies assigned to you for assessment.
                    </p>
                </div>
            </div>

            {/* Table */}
            <ReusableTable
                title="Assigned Revised Copies"
                subTitle="Revised copies assigned for your assessment"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, submission number..."
                emptyMessage="No revised copy submissions assigned to you."
                showSearch={false}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── Revision History Drawer ─────────────────────────────────── */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={() => { 
                    setIsDrawerOpen(false); 
                    setDrawerItems([]); 
                    setSelectedSubmissionTitle('');
                    setSelectedSubmissionNumber('');
                }}
                title="Revision History"
                width="max-w-2xl"
            >
                {/* Submission info header */}
                {(selectedSubmissionTitle || selectedSubmissionNumber) && (
                    <div className="mb-5 pb-4 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{selectedSubmissionTitle}</p>
                        <p className="text-xs text-gray-400 font-mono mt-0.5">{selectedSubmissionNumber}</p>
                    </div>
                )}

                {drawerLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                    </div>
                ) : drawerItems.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-12">No revision records found for this submission.</p>
                ) : (
                    <div className="space-y-5 pb-10">
                        <p className="text-sm text-gray-500">
                            {drawerItems.length} revision request{drawerItems.length !== 1 ? 's' : ''} for this submission
                        </p>
                        {drawerItems.map((item, idx) => (
                            <div key={item.submissionRevisionRequestId} className="border border-gray-100 rounded-xl p-5 bg-white shadow-sm space-y-4">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">{item.submissionTitle}</p>
                                        <p className="text-xs text-gray-400 font-mono mt-0.5">{item.submissionNumber}</p>
                                    </div>
                                    <StatusBadge status={item.status} />
                                </div>

                                {/* Authors */}
                                {item.bookAuthors && item.bookAuthors.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Authors</p>
                                        <p className="text-sm text-gray-700">{item.bookAuthors.map(a => a.name).join(', ')}</p>
                                    </div>
                                )}

                                {/* Dates */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Requested</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.requestedAt)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Submitted</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.submittedAt)}</p>
                                    </div>
                                </div>

                                {/* Documents */}
                                <div className="space-y-2">
                                    {item.reviewedManuscriptUrl && (
                                        <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Reviewed Manuscript</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`reviewed-${idx}`]}
                                                onClick={() => handleDownload(
                                                    item.reviewedManuscriptUrl!,
                                                    `reviewed-${idx}`,
                                                    `${item.submissionNumber}_reviewed.docx`
                                                )}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`reviewed-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                    {item.revisedVersionUrl && (
                                        <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Revised Version (Author)</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`revised-${idx}`]}
                                                onClick={() => handleDownload(
                                                    item.revisedVersionUrl!,
                                                    `revised-${idx}`,
                                                    `${item.submissionNumber}_revised_v${idx + 1}.docx`
                                                )}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-green-200 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`revised-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Comment */}
                                {item.comment && (
                                    <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Admin Comment</p>
                                        <p className="text-sm text-gray-700">{item.comment}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>

            {/* ── Perform Revision Assessment Modal ───────────────────────────── */}
            <Modal
                isOpen={isAssessmentModalOpen}
                onClose={closeAssessmentModal}
                title="Perform Revision Assessment"
                description="Provide your assessment recommendation for this revised copy submission."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {/* Submission Info */}
                    {assessmentTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{assessmentTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{assessmentTarget.submissionNumber}</p>
                        </div>
                    )}

                    {/* Warning Message */}
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">
                            This assessment is <strong>final</strong> and cannot be modified after submission.
                        </p>
                    </div>

                    {/* Recommendation Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Recommendation <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={assessmentRecommendation}
                            onChange={(e) => setAssessmentRecommendation(e.target.value as '' | '2' | '3')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                        >
                            <option value="">Select recommendation...</option>
                            <option value="2">Accept</option>
                            <option value="3">Need Revision</option>
                        </select>
                    </div>

                    {/* Comment Textarea */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={assessmentComment}
                            onChange={(e) => setAssessmentComment(e.target.value)}
                            placeholder="Provide additional feedback or notes for this assessment..."
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            onClick={closeAssessmentModal}
                            disabled={isSubmittingAssessment}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitAssessment}
                            disabled={isSubmittingAssessment || !assessmentRecommendation}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmittingAssessment ? (
                                <><Spinner />Submitting...</>
                            ) : (
                                'Submit Assessment'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default AssignedRevisionAssessments;